import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
    IMaterial,
    IproductQueueObject,
    ItableFiltersParams,
    ThunkResult
} from '../models';
import { beginAjaxCall, endAjaxCall } from './ajaxStatusActions';
import API from '../constants/apiEndpoints';
import { constants } from '../constants/constants';
import * as types from './actionTypes';
import { msalFetch } from '../components/auth/Auth-Utils';

/**
 * Normalize GetProductQueue response: API returns materialID/material; app expects productID/product.
 * Supports both shapes so reducer and consumers work unchanged.
 */
function normalizeProductQueueResponse(items: any[]): any[] {
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => ({
        ...item,
        productID: item.materialID != null ? item.materialID : item.productID,
        product: item.material != null ? item.material : item.product
    }));
}

let productQueueCancelSource: ReturnType<
    typeof Axios.CancelToken.source
> | null = null;

export function getProductQueue(): ThunkResult<any> {
    return (dispatch, getState) => {
        if (productQueueCancelSource) {
            productQueueCancelSource.cancel();
        }
        productQueueCancelSource = Axios.CancelToken.source();

        dispatch(beginAjaxCall());
        const {
            page,
            search,
            mainCategory,
            subCategory,
            brand,
            origin,
            standard
        } = getState().manageProductQueue.tableFilters;
        // `standard` may be a single option or an array (see ItableFiltersReducer). Normalize so
        // the 'no-standard' option maps to `noStandardOnly` and real standards go to `standards`.
        const selectedStandards = Array.isArray(standard)
            ? standard
            : standard
            ? [standard]
            : [];
        const noStandardOnly = selectedStandards.some(
            o => o.value === 'no-standard'
        );
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            cancelToken: productQueueCancelSource.token,
            data: {
                page: page || 1,
                search: search || '',
                mainCategoryID: mainCategory ? mainCategory.value : null,
                subCategoryID: subCategory ? subCategory.value : null,
                brandID: brand ? brand.value : null,
                origin: origin ? origin.value : null,
                standards: selectedStandards
                    .filter(o => o.value !== 'no-standard')
                    .map(o => o.value),
                noStandardOnly: noStandardOnly || null
            }
        };

        const url = API.GET.inventory.getproductqueue;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    const normalized = normalizeProductQueueResponse(
                        data.data.result || []
                    );
                    dispatch({
                        type: types.PRODUCT_QUEUE_SUCCESS,
                        payload: { productQueueObjects: normalized }
                    });
                    dispatch({
                        type: types.PRODUCT_QUEUE_TOTAL_PAGES,
                        pages: data.data.pages
                    });
                    return data;
                }
            })
            .catch((error: any) => {
                if (Axios.isCancel(error)) {
                    dispatch(endAjaxCall());
                    return;
                }
                dispatch({
                    type: types.PRODUCT_QUEUE_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'get product queue');
                console.error(error);
            });
    };
}

export function getMergeableProducts({
    page,
    search,
    mainCategoryID,
    subCategoryID,
    brandID,
    origin,
    isFinalProduct,
    standards,
    isApproved
}: {
    page: number;
    search: string | null;
    mainCategoryID: string | null;
    subCategoryID: string | null;
    brandID: string | null;
    origin: string | null;
    isFinalProduct: boolean;
    standards: string[];
    isApproved?: boolean;
}): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch({ type: types.MERGEABLE_PRODUCTS_REQUEST });
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: {
                page: page || 1,
                search: search || '',
                mainCategoryID: mainCategoryID || null,
                subCategoryID: subCategoryID || null,
                brandID: brandID || null,
                origin: origin || null,
                isFinalProduct: isFinalProduct || false,
                standards: standards || [],
                isApproved: isApproved || null
            }
        };
        const url = API.GET.inventory.getproductqueue;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    const normalized = normalizeProductQueueResponse(
                        data.data.result || []
                    );
                    dispatch({
                        type: types.MERGEABLE_PRODUCTS_SUCCESS,
                        payload: { productQueueObjects: normalized }
                    });
                }
                return data;
            })
            .catch((error: any) => {
                dispatch({
                    type: types.MERGEABLE_PRODUCTS_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'get mergeable product queue');
                console.error(error);
            });
    };
}

export function approveProduct(queueID: string, dispatch: any) {
    const axiosOptions: AxiosRequestConfig = {
        method: 'post',
        data: { id: queueID }
    };

    const url = API.POST.inventory.approveproduct;
    return msalFetch(url, axiosOptions)
        .then((data: AxiosResponse<any>) => {
            if (!data.data) {
                throw new Error('missing data');
            } else {
                dispatch({
                    type: types.PRODUCT_APPROVE_SUCCESS,
                    payload: { queueID }
                });
            }
        })
        .catch((error: any) => {
            dispatch({
                type: types.PRODUCT_APPROVE_FAILED,
                error,
                axiosOptions
            });
            constants.handleError(error, 'approve product');
            console.error(error);
        });
}

export function updateQueueProduct(
    product: IMaterial,
    shouldApprove?: boolean,
    queueID?: string
): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        dispatch({ type: types.TOGGLE_MODAL_EDIT_PRODUCT });
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: product
        };

        const url = API.POST.inventory.updateproduct;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    dispatch({
                        type: types.PRODUCT_QUEUE_PRODUCT_UPDATE_SUCCESS,
                        payload: { product: data.data, queueID }
                    });
                    // toastr.success('Success', 'Saved product', constants.toastrSuccess);
                    if (shouldApprove && queueID) {
                        dispatch(beginAjaxCall());
                        approveProduct(queueID, dispatch); // don't return this because if we do, we will see two errors
                    }
                }
            })
            .catch((error: any) => {
                dispatch({
                    type: types.PRODUCT_QUEUE_PRODUCT_UPDATE_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'update queue product');
                console.error(error);
            });
    };
}

export const toggleApproveProductModal = () => ({
    type: types.TOGGLE_MODAL_APPROVE_PRODUCT
});

export const setTableFilter = (filters: ItableFiltersParams) => ({
    type: types.SET_TABLE_FILTER_MANAGE_PRODUCT_QUEUE,
    filters
});

export const setSelectedQueueObject = (queueObject: IproductQueueObject) => ({
    type: types.SET_SELECTED_QUEUE_OBJECT,
    payload: queueObject
});
